import type { PvpAbandonCause, PvpEndedFrame, PvpEndReason, PvpSeat } from '@kenocyber/shared';

/**
 * What a player is told when a duel ends.
 *
 * ## Why this is a pure function with no default branch
 *
 * A `default:` here is the one bug worth designing against: an end reason
 * nobody handled would fall through to whatever the nearest case is, and the
 * nearest case in a result screen is a victory. `service_failure` in
 * particular - the room admitting it cannot vouch for its own state - must
 * never be shown as somebody winning.
 *
 * So the switch is exhaustive and its fallthrough is `never`. A tenth reason
 * added to the contract fails `typecheck` here before it can reach a screen.
 *
 * ## It knows nothing about rewards, because there are none
 *
 * A duel grants no Gold, no Cash, no XP and no item. There is no field on this
 * type for one, and nothing in this module could produce a number to put in it.
 */

export type PvpOutcome = 'win' | 'loss' | 'draw' | 'neutral' | 'failure';

/**
 * Who the abandoning participant is TO THE READER of this particular screen.
 *
 * The server states one fact - this chair, this cause - and the six browsers in
 * a team match read it six ways. `unknown` is the honest fourth value: a frame
 * carrying no cause, or a client that never learned its own chair, has nothing
 * to compare and must say so rather than default into "somebody else".
 */
export type PvpAbandonRelation = 'self' | 'ally' | 'opponent' | 'unknown';

/**
 * The one sentence that says what happened, for every relation and both causes.
 *
 * ## Why the two rulesets share it
 *
 * `pvpResultText` and `pvpTeamResultText` are deliberately separate because they
 * compare different things - a seat against a seat, a team against a team. What
 * they are NOT allowed to differ on is the words: a duel and a team match that
 * described the same event in two vocabularies would be two products. So the
 * comparison stays in each, and the sentence lives once, here.
 *
 * ## Why `unknown` does not name a cause
 *
 * "A partida terminou por abandono." is all a client can truthfully say about a
 * frame that did not tell it which abandonment this was. It is not a placeholder
 * to be filled in later by a guess; it is the complete and correct answer to a
 * question the server of that frame could not answer.
 */
export function pvpAbandonSentence(
  cause: PvpAbandonCause | null,
  relation: PvpAbandonRelation,
): string {
  if (cause === null || relation === 'unknown') {
    return 'A partida terminou por abandono.';
  }
  if (cause === 'voluntary') {
    return relation === 'self'
      ? 'Você abandonou a partida.'
      : relation === 'ally'
        ? 'Um aliado abandonou a partida.'
        : 'Adversário abandonou a partida.';
  }
  return relation === 'self'
    ? 'Você não voltou dentro da janela de reconexão.'
    : relation === 'ally'
      ? 'Um aliado não voltou dentro da janela de reconexão.'
      : 'Adversário não voltou dentro da janela de reconexão.';
}

export interface PvpResultText {
  /** The headline. Written for a player, in the game's own language. */
  readonly title: string;
  /** One line saying why, and never more than the server actually said. */
  readonly detail: string;
  /** Drives colour and emphasis. Never a payout, because there is not one. */
  readonly outcome: PvpOutcome;
}

/**
 * Turns an ending into words.
 *
 * The winner is compared against the LOCAL seat rather than assumed: the two
 * clients in one duel receive the same frame and must read it differently, and
 * getting that backwards would tell the loser they won.
 */
export function pvpResultText(result: PvpEndedFrame, localSeat: PvpSeat | null): PvpResultText {
  const reason: PvpEndReason = result.reason;
  const won = result.winner !== null && result.winner === localSeat;

  switch (reason) {
    case 'death':
      return won
        ? { title: 'VITÓRIA', detail: 'A nave adversária foi destruída.', outcome: 'win' }
        : { title: 'DERROTA', detail: 'Sua nave foi destruída.', outcome: 'loss' };

    case 'abandonment': {
      /*
       * The DETAIL now comes from the server's fact, and the title does not.
       *
       * Both lines used to be decided by `won`, and that is how a player who had
       * just pressed ABANDONAR was told they had failed to reconnect: losing an
       * abandonment was assumed to mean having been the one who was away. It
       * meant nothing of the kind. The title still follows the authoritative
       * winner - that part was always right - and the sentence follows the
       * authoritative cause and chair.
       *
       * A duel has no third relation: the seat that abandoned is either this
       * client's or the only other one. A client with no seat of its own has
       * nothing to compare and gets the sentence that claims nothing.
       */
      const abandon = result.abandon ?? null;
      const detail = pvpAbandonSentence(
        abandon?.cause ?? null,
        abandon === null || localSeat === null
          ? 'unknown'
          : abandon.seat === localSeat
            ? 'self'
            : 'opponent',
      );
      return won
        ? { title: 'ADVERSÁRIO ABANDONOU', detail, outcome: 'win' }
        : { title: 'DERROTA', detail, outcome: 'loss' };
    }

    case 'mutual_destruction':
      return {
        title: 'EMPATE',
        detail: 'As duas naves foram destruídas no mesmo tick.',
        outcome: 'draw',
      };

    case 'time_limit':
      return {
        title: 'EMPATE POR TEMPO',
        detail: 'O tempo acabou com as duas naves de pé.',
        outcome: 'draw',
      };

    case 'cancelled_by_challenger':
      return {
        title: 'PARTIDA CANCELADA',
        detail: 'O desafio foi retirado antes de alguém entrar.',
        outcome: 'neutral',
      };

    case 'expired_unaccepted':
      return {
        title: 'CONVITE EXPIRADO',
        detail: 'Ninguém entrou com o código a tempo.',
        outcome: 'neutral',
      };

    case 'service_failure':
      /*
       * NEVER a victory, and never a defeat either.
       *
       * The room could not vouch for its own state, so it named no winner - and
       * inventing one from whichever ship happened to be ahead would be a guess
       * wearing a result's clothes. The player is told the truth: the match
       * failed, and nothing was decided.
       */
      return {
        title: 'FALHA DA PARTIDA',
        detail: 'A sala não pôde concluir o duelo. Nenhum vencedor foi declarado.',
        outcome: 'failure',
      };

    default: {
      /*
       * Unreachable, and enforced by the compiler.
       *
       * If the server's contract gains a tenth reason, this line stops
       * compiling - which is the point. The alternative is a screen that shows
       * something plausible for a reason nobody thought about.
       */
      const unhandled: never = reason;
      throw new Error(`unhandled pvp end reason: ${String(unhandled)}`);
    }
  }
}

/**
 * Whether the server named a winner at all.
 *
 * A separate question from who won: a draw and a match with no trustworthy
 * result both have no winner, and only the REASON tells them apart. The screen
 * uses this to decide whether to name a seat, never to decide an outcome.
 */
export function pvpNamesAWinner(result: PvpEndedFrame): boolean {
  return result.winner !== null;
}
